import React from 'react';
import { Row, List, Col, Button, Divider } from 'antd';
import styled from 'styled-components';
import Link from 'next/link';
import NImage from 'next/image';
import Image from 'components/Image';
import { SectionTitle, SubTitle } from 'components/Section';
import EventCard from 'components/EventCard';
import SocialLinks from 'components/SocialLinks';
import RelativeContentTabs from 'components/RelativeContentTabs';
import Section from 'components/Section';
import { SkyscraperMobile } from 'components/Ads';
import DefaultBG from 'public/static/images/default_company_bg.png';
import dynamic from 'next/dynamic';
import { merriweather } from 'styles/GlobalStyle';

const DynamicForm = dynamic(() => import('components/DynamicForm'), {
    ssr: false
});

interface GroupedByBranchs {
    [key: string]: {
        title: string;
        seoSlug: string;
        items: [
            {
                title: string;
                seoSlug: string;
            }
        ];
    };
}

const ListStyled = styled(List)`
    .ant-list-item {
        padding: 0px 16px;
    }
    .ant-list-header {
        padding-top: 12px;
        padding-bottom: 6px;
    }
`;

const BranchTitle = styled.h5`
    font-size: 18px;
    font-weight: 600;
    font-stretch: normal;
    font-style: normal;
    line-height: 1.56;
    letter-spacing: normal;
    text-align: left;
    margin-bottom: 0;
    margin: 0;
    padding: 0;
    a {
        color: ${({ theme }) => theme.colors.greyishBrown};
    }
`;

const BranchName = styled.h6`
    font-size: 18px;
    font-weight: 300;
    font-stretch: normal;
    font-style: normal;
    line-height: 1.56;
    letter-spacing: normal;
    text-align: left;
    margin: 0;
    a {
        color: ${({ theme }) => theme.colors.greyishBrown};
    }
`;

const LogoContainer = styled.div`
    position: absolute;
    height: auto;
    top: 50%;
    left: 3%;
    border: 1px solid #b7cbd9;
    @media (max-width: 992px) {
        top: 55%;
        left: 50%;
        transform: translateX(-50%);
        > img {
            max-width: 140px;
        }
    }
    @media (max-width: 576px) {
        top: 15%;
    }
`;

const CompanyName = styled.h1`
    font-size: 32px;
    font-weight: bold;
    font-stretch: normal;
    font-style: normal;
    line-height: 1.4;
    letter-spacing: normal;
    text-align: left;
    margin: 0;
    color: ${({ theme }) => theme.colors.brownishGrey};
    @media (max-width: 992px) {
        text-align: center;
    }
`;

const CompanyAddress = styled.h2`
    font-size: 20px;
    font-weight: 300;
    font-stretch: normal;
    font-style: normal;
    line-height: 1.4;
    letter-spacing: normal;
    text-align: left;
    margin: 0;
    color: ${({ theme }) => theme.colors.primary};
    @media (max-width: 992px) {
        text-align: center;
    }
`;

const CompanyURL = styled.p`
    font-size: 20px;
    font-weight: 300;
    font-stretch: normal;
    font-style: normal;
    line-height: 1.4;
    letter-spacing: normal;
    text-align: left;
    margin: 0;
    a {
        color: ${({ theme }) => theme.colors.brownishGrey};
    }
    @media (max-width: 992px) {
        text-align: center;
    }
`;

const AboutTitle = styled.p`
    font-family: ${merriweather.style.fontFamily};
    text-transform: uppercase;
    font-size: 18px;
    font-weight: bold;
    font-stretch: normal;
    font-style: normal;
    line-height: 2.22;
    letter-spacing: 0.36px;
    text-align: left;
    color: ${({ theme }) => theme.colors.primary};
`;

const AboutText = styled.div`
    font-family: ${merriweather.style.fontFamily};
    font-size: 18px;
    font-weight: 300;
    font-stretch: normal;
    font-style: normal;
    line-height: 1.56;
    letter-spacing: normal;
    text-align: left;
    color: ${({ theme }) => theme.colors.brownishGrey};
`;

const HeadingCol = styled(Col)`
    @media (max-width: 992px) {
        margin-bottom: 65px;
    }
`;

const groupByParentBranch = (branches: Branch[]): GroupedByBranchs => {
    return branches.reduce((r, a) => {
        if (a.lvl === 1) {
            r[a.seoSlug] = {
                title: a.title,
                seoSlug: a.seoSlug,
                items: []
            };
        } else {
            r[a.parent.seoSlug] = {
                title: a.parent.title,
                seoSlug: a.parent.seoSlug,
                items: [
                    ...(r[a.parent.seoSlug] || { items: [] }).items,
                    { title: a.title, seoSlug: a.seoSlug }
                ]
            };
        }
        return r;
    }, {});
};

interface Props extends Company {
    ads: PageAds;
}

const CompanyDetails = ({
    name,
    logo,
    seoSlug,
    about,
    branches,
    addresses,
    socialLinks,
    premium,
    premiumBackground,
    additionalInfo,
    articles,
    galleries,
    videos,
    url,
    whitePapers,
    events,
    sideForm,
    ads
}: Props): React.JSX.Element => {
    const groupedBranches = Object.values(groupByParentBranch(branches));

    const address = addresses && addresses.length > 0 ? addresses[0] : undefined;

    const sortedEvents = events.sort((a: Occurrence, b: Occurrence): number => {
        return new Date(b.endDate).getTime() - new Date(a.endDate).getTime();
    });

    const { skyscraper } = ads;
    return (
        <>
            <Row style={{ position: 'relative' }}>
                <HeadingCol span={24}>
                    {premium && premiumBackground && (
                        <Image
                            type="billboard"
                            width={1200}
                            height={303}
                            src={premiumBackground}
                            alt={name}
                            style={{ maxWidth: '100%', objectFit: 'contain' }}
                        />
                    )}

                    {(!premium || !premiumBackground) && (
                        <NImage
                            width={1200}
                            height={303}
                            src={DefaultBG}
                            alt={name}
                            style={{ maxWidth: '100%', objectFit: 'contain' }}
                        />
                    )}

                    <LogoContainer>
                        <Image
                            type="logo"
                            width={270}
                            height={270}
                            src={logo}
                            alt={name}
                            style={{ maxWidth: '100%', objectFit: 'contain' }}
                        />
                    </LogoContainer>
                </HeadingCol>
            </Row>
            <Section>
                <Col
                    xs={{ span: 24, order: 2 }}
                    lg={{ offset: 8, span: 11, order: 1 }}
                    xxl={{ offset: 7, span: 11, order: 1 }}
                >
                    <Row>
                        <Col span={24}>
                            <CompanyName>{name}</CompanyName>
                            {address && (
                                <CompanyAddress>{`${address.street}, ${address.zip} ${address.city}, ${address.country}`}</CompanyAddress>
                            )}
                            {url && (
                                <CompanyURL>
                                    <a target="_blank" href={url} rel="noopener noreferrer">
                                        {url}
                                    </a>
                                </CompanyURL>
                            )}
                        </Col>
                        {premium && (
                            <Col span={24}>
                                <Divider
                                    style={{
                                        margin: '5px 0 24px 0',
                                        width: '60%',
                                        minWidth: '60%'
                                    }}
                                />
                                <Link href={`/katalog-firm/${seoSlug}/kontakt`}>
                                    <Button style={{ padding: '0 30px' }}>Kontakt</Button>
                                </Link>
                            </Col>
                        )}
                    </Row>
                </Col>
                <Col
                    xs={{ span: 24, order: 1 }}
                    lg={{ span: 5, order: 2 }}
                    xxl={{ span: 6, order: 2 }}
                >
                    <Row justify="end" wrap>
                        <Col>
                            <SocialLinks {...socialLinks} />
                        </Col>
                    </Row>
                </Col>
            </Section>
            <Divider />
            <Row justify="center">
                <Col xs={24} sm={12}>
                    <SkyscraperMobile id={skyscraper[0]} margin="12px auto 12px auto" />
                </Col>
                <Col xs={24} sm={12}>
                    <SkyscraperMobile id={skyscraper[0]} margin="12px auto 12px auto" />
                </Col>
            </Row>
            <Section gutter={16}>
                <Col xs={24} lg={18}>
                    <Row>
                        <Col xs={24} lg={6}>
                            <AboutTitle>O FIRMIE</AboutTitle>
                        </Col>
                        <Col xs={24} lg={18}>
                            <Row>
                                <Col span={24}>
                                    <AboutText dangerouslySetInnerHTML={{ __html: about }} />
                                    {premium && (
                                        <AboutText
                                            dangerouslySetInnerHTML={{ __html: additionalInfo }}
                                        />
                                    )}
                                </Col>
                            </Row>
                        </Col>
                        {premium && (
                            <>
                                <Divider />
                                <Col span={24} style={{ padding: '25px 0 0 0' }}>
                                    <AboutTitle>Informacje firmowe</AboutTitle>
                                    <RelativeContentTabs
                                        articles={articles}
                                        galleries={galleries}
                                        videos={videos}
                                        whitePapers={whitePapers}
                                        itemsPerTab={999}
                                    />
                                </Col>
                            </>
                        )}
                    </Row>
                </Col>

                <Col xs={24} xl={6}>
                    <Row>
                        <Col span={24}>
                            <SectionTitle title="Branże" />
                        </Col>
                        {groupedBranches.map((branch) => {
                            if (branch.items.length > 0) {
                                return (
                                    <Col span={24} key={branch.seoSlug}>
                                        <ListStyled
                                            split={false}
                                            rowKey={({ seoSlug }: Branch): string => seoSlug}
                                            size="small"
                                            header={
                                                <BranchTitle>
                                                    <Link
                                                        href={`/katalog-firm?branch=${branch.seoSlug}`}
                                                    >
                                                        {branch.title}
                                                    </Link>
                                                </BranchTitle>
                                            }
                                            dataSource={branch.items}
                                            renderItem={(item: Branch) => (
                                                <List.Item>
                                                    <BranchName>
                                                        <Link
                                                            href={`/katalog-firm?branch=${item.seoSlug}`}
                                                        >
                                                            {item.title}
                                                        </Link>
                                                    </BranchName>
                                                </List.Item>
                                            )}
                                        />
                                    </Col>
                                );
                            }
                            return (
                                <Col span={24} key={branch.seoSlug}>
                                    <BranchTitle>
                                        <Link href={`/katalog-firm?branch=${branch.seoSlug}`}>
                                            {branch.title}
                                        </Link>
                                    </BranchTitle>
                                </Col>
                            );
                        })}
                    </Row>
                    <Divider />
                    {sideForm && (
                        <Section>
                            <Col span={24}>
                                <DynamicForm form={sideForm} />
                            </Col>
                        </Section>
                    )}
                    {sortedEvents?.length > 0 && (
                        <Section>
                            <Col span={24}>
                                <SectionTitle
                                    title="Wydarzenia"
                                    url="/events"
                                    style={{ marginBottom: 0 }}
                                />

                                <List
                                    itemLayout="horizontal"
                                    dataSource={sortedEvents}
                                    rowKey={({ seoSlug }: Occurrence): string => seoSlug}
                                    header={
                                        <Col span={16}>
                                            <SubTitle
                                                category="Powiązane z firmą"
                                                style={{ margin: 0 }}
                                            />
                                        </Col>
                                    }
                                    renderItem={(item: Occurrence) => (
                                        <List.Item>
                                            <EventCard
                                                title={item.name}
                                                type={item.type}
                                                slug={item.seoSlug}
                                                startDate={item.startDate}
                                                endDate={item.endDate}
                                            />
                                        </List.Item>
                                    )}
                                />
                            </Col>
                        </Section>
                    )}
                    <Section>
                        <Col span={24}>{/* <HalfPage /> CMS */}</Col>
                    </Section>
                </Col>
            </Section>
            <Row justify="center">
                <Col>
                    {/* <Wideboard
                        id="1581938133657-0"
                        name="wideboard_standard"
                        targeting={{
                            key: 'wideboard_stand_nr',
                            value: ['1']
                        }}
                    /> CMS */}
                </Col>
            </Row>
        </>
    );
};

export default CompanyDetails;
